import { action } from '@ember/object';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type {
	APIData,
	BlockThemeConfig,
	CreateRecordProperties,
	DateRangeInterface,
	DSSetup,
	Service,
} from 'webapp-types';
import ThemeConfig from 'webapp/const/theme-config';
import {
	BlockVisualization,
	ChartLegendPosition,
	CompareType,
	DataSelectType,
	GranulationTypeId,
	SortFieldType,
	TableColumnType,
	TimeUnit,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, equal, sortBy } from 'webapp/utils/common';

import type { Option as ColorOption } from 'webapp/components/common/popups/popup-colors';

import type Block from 'webapp/models-custom/block';
import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import Serviced from 'webapp/models-custom/serviced';
import VisualizationAiSummary from 'webapp/models-custom/visualization/ai-summary';
import VisualizationBarChart from 'webapp/models-custom/visualization/bar-chart';
import VisualizationBubble from 'webapp/models-custom/visualization/bubble';
import VisualizationDataStory from 'webapp/models-custom/visualization/data-story';
import VisualizationFunnel from 'webapp/models-custom/visualization/funnel';
import VisualizationGauge from 'webapp/models-custom/visualization/gauge';
import VisualizationHeatmap from 'webapp/models-custom/visualization/heatmap';
import VisualizationHorizontalBarChart from 'webapp/models-custom/visualization/horizontal-bar-chart';
import VisualizationImage from 'webapp/models-custom/visualization/image';
import VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import VisualizationNumber from 'webapp/models-custom/visualization/number';
import VisualizationPie from 'webapp/models-custom/visualization/pie';
import VisualizationPipeline from 'webapp/models-custom/visualization/pipeline';
import VisualizationProgress from 'webapp/models-custom/visualization/progress';
import VisualizationRadialProgress from 'webapp/models-custom/visualization/radial-progress';
import VisualizationSpider from 'webapp/models-custom/visualization/spider';
import VisualizationTable from 'webapp/models-custom/visualization/table';
import VisualizationText from 'webapp/models-custom/visualization/text';
import type BlockRecord from 'webapp/models/block';
import type { ColorSetting } from 'webapp/models/block';
import type DataSelectRecord from 'webapp/models/data-select';

import ModelManagerDataSelectsService from 'webapp/services/model-manager/data-selects';

export type AnyVisualization =
	| VisualizationNumber
	| VisualizationLineChart
	| VisualizationBarChart
	| VisualizationFunnel
	| VisualizationPipeline
	| VisualizationProgress
	| VisualizationTable
	| VisualizationPie
	| VisualizationRadialProgress
	| VisualizationHorizontalBarChart
	| VisualizationDataStory
	| VisualizationHeatmap
	| VisualizationBubble
	| VisualizationGauge
	| VisualizationSpider
	| VisualizationText
	| VisualizationImage
	| VisualizationAiSummary;

export type ChangeVisualizationWithPossibleConfirmFnReturnType = (() => void) | null;

export type CustomColorMap = Record<string, string>;

type EventFunction = () => void;

// properties of a new data select of this block; the metric insights' fake data selects use
// their uuid strings for blockId and updateFrom
export type DataSelectProps = Omit<
	CreateRecordProperties<DataSelectRecord>,
	'blockId' | 'updateFrom'
> & {
	blockId?: number | string | null;
	updateFrom?: number | string | null;
};

// BlockSetup is somewhat a "middle-man" between block and its Visualization.
// All DataSelect creations, updates and deletes go through BlockSetup,
// to allow for extra logic coming from Visualization models -
// e.g. enable something on block when adding a new metric (DataSelect).
// BlockSetup also handles visualization switching by involving Visualization models (from / to) and their methods,
// and helps in block color management (which are available, which are used, etc).
// Read more: https://databox.atlassian.net/wiki/x/AgAb3Q

export default class BlockSetup extends Serviced {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('logger') declare loggerService: Service.Logger;
	@service('constants') declare constantsService: Service.Constants;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	block: Block;

	isAutosaveOn = true;

	@tracked visualization!: AnyVisualization;

	constructor(block: Block) {
		super();
		this.block = block;

		this.recreateVisualization(true);
	}

	get isTemporaryBlock(): boolean {
		return (
			this.block.isFromCustomQuery ||
			this.block.isFromDataCalculation ||
			this.block.isFromMetricInsight
		);
	}

	private _dataSelects: DataSelect[] = [];

	@cached
	get dataSelects(): DataSelect[] {
		const dataSelectRecords = this.mmDataSelectsService.getRecordsForBlock(
			this.block.numericId!,
		);
		ModelManagerDataSelectsService.updateModelCacheHelper(
			this._dataSelects,
			dataSelectRecords,
			this.mmDataSelectsService.createModel,
		);
		this._dataSelects.forEach((ds) => {
			if (ds.block !== this.block) {
				ds.block = this.block;
			}
		});
		return this._dataSelects;
	}

	@cached
	get dataSelectsShown(): DataSelect[] {
		// no updateFrom and type 0
		return this.dataSelects.filter((item) => item.isPrimaryMain);
	}

	get primaryMainDataSelects(): DataSelect[] {
		return this.dataSelects.filter((item) => item.isPrimaryMain);
	}

	get primaryDataSelects(): DataSelect[] {
		return this.dataSelects.filter((item) => item.isPrimary);
	}

	get secondaryDataSelects(): DataSelect[] {
		// has updateFrom
		return this.dataSelects.filter((item) => !item.isPrimary);
	}

	@cached
	get sortedDataSelectsShown(): DataSelect[] {
		return sortBy(this.dataSelectsShown, 'order', 'id');
	}

	@cached
	get firstShownDataSelect(): DataSelect | null {
		return this.sortedDataSelectsShown[0] || null;
	}

	@cached
	get allowAdvancedSetup() {
		return this.dataSelects.some((ds) => ds.allowAdvancedSetup);
	}

	@cached
	get allowAdvancedFilters() {
		return this.dataSelects.some((ds) => ds.allowAdvancedFilters);
	}

	getSecondaryDataSelectsForDataSelect(ds: DataSelect): DataSelect[] {
		return this.secondaryDataSelects.filter((item) => item.updateFrom === ds.numericId);
	}

	getPrimaryDataSelectForDataSelect(ds: DataSelect): DataSelect | undefined {
		return this.primaryDataSelects.find((pds) => pds.numericId === ds.updateFrom);
	}

	getDataSelectById(id: number | string) {
		return this.dataSelects.find((item) => item.id === String(id));
	}

	getNextOrder(): number {
		const orders = this.dataSelectsShown.map((ds) => ds.order || 0);
		return Math.max(...orders) + 1;
	}

	getCompareIndexOnPrimaryDs(secondaryDs: DataSelect): number {
		// from secondaryDs that are created as "compare" series
		// we try to identify which compare on primaryDs it's "connected" to
		// this is highly inprecise and used only in tooltip logic
		const allPrimaryDsCompares =
			this.primaryDataSelects.find((item) => item.id === String(secondaryDs.updateFrom))
				?.compares || [];

		let firstMatchingCompare: DSSetup.Compare | null = null;
		if (secondaryDs.isGoal) {
			firstMatchingCompare =
				allPrimaryDsCompares.find((c) => c.type === CompareType.GOAL) || null;
		} else if (secondaryDs.isCompare) {
			firstMatchingCompare =
				allPrimaryDsCompares.find((c) => c.type !== CompareType.GOAL) || null;
		}

		return firstMatchingCompare ? allPrimaryDsCompares.indexOf(firstMatchingCompare) : -1;
	}

	addSecondaryDataSelectForDataSelect(
		primaryDataSelect: DataSelect,
		type: DataSelectType,
		extraProps: DataSelectProps = {},
	): DataSelect | null {
		// used to add "additional lines"
		const props: DataSelectProps = Object.assign(
			{
				type,
				updateFrom: primaryDataSelect.numericId,
				dateRange: primaryDataSelect.limitations.dateRange?.getFormula(),
				metricSetupKey: primaryDataSelect.metricSetupKey,
				metricSetupSpaceAccessId: primaryDataSelect.metricSetupSpaceAccessId,
				metricSetupDimensions: primaryDataSelect.metricSetupDimensions,
				metricSetupFilters: primaryDataSelect.metricSetupFilters,
			},
			extraProps,
		);

		if (type === DataSelectType.COMPARE) {
			const firstCompare = primaryDataSelect.limitations.compareCMs[0];

			if (!firstCompare || firstCompare.isNone) {
				return null;
			}

			const compareFormula = firstCompare.getFormula()!;
			if (compareFormula) {
				if (firstCompare.isOtherMetric) {
					const metricSetup = compareFormula.metricSetup;
					if (metricSetup) {
						props.metricSetupKey = metricSetup.key;
						props.metricSetupSpaceAccessId = metricSetup.spaceAccessId;
						props.metricSetupDimensions = metricSetup.dimensions;
						props.metricSetupFilters = metricSetup.filters;
					}
				} else {
					if (firstCompare.isGoal) {
						// when adding Compare line with first compare being Goal, we add Previous compare line instead
						const newCompare = DsCompare.createFromFormula(null);
						newCompare.updateFromTypeId(
							DsCompare.customPreviousId,
							primaryDataSelect.limitations.dateRange!,
						);
						props.dateRange!.offset = newCompare.getFormula()?.dateRange?.offset; // copied from the primary
					} else {
						// read dateRange from compare itself
						const dateRangeFormula = compareFormula?.dateRange;
						if (dateRangeFormula) {
							if (dateRangeFormula.range) {
								props.dateRange!.range = dateRangeFormula.range;
							}
							if (dateRangeFormula.offset) {
								props.dateRange!.offset = dateRangeFormula.offset;
							}
						}
					}
				}
			}

			props.isStacked = primaryDataSelect.isStacked;
			props.visualization = primaryDataSelect.visualization;
		}

		return this.createDataSelect(props);
	}

	changeSortField(sortField: SortFieldType): void {
		const block = this.block;
		block.sortField = sortField;
		if (sortField === SortFieldType.CUSTOM) {
			block.sortOrder = null;
		}
		this.trackBlockPropertySettingsChangeEvent({ sort_by: sortField });
		block.blockSetup.saveBlock();
	}

	// ------ COLORS PER DS / ITEM ------

	get themeConfig(): BlockThemeConfig.Type {
		let themeConfig;
		const boardColorConfigThemeId =
			this.block.boardObj?.colorConfig.themeId ||
			this.block.reportSlideFragment?.blockThemeId ||
			this.block.themeId;
		if (boardColorConfigThemeId) {
			themeConfig = ThemeConfig.find((item) => item.id === boardColorConfigThemeId);
		}
		if (!themeConfig) {
			themeConfig = ThemeConfig[0]!;
		}
		return themeConfig;
	}

	get allAvailableColorsIndexes(): number[] {
		// works only for the ones that don't have special settings (line, bar, pie, ..)
		const visualizationsConfig = this.themeConfig.visualizations;
		if (visualizationsConfig) {
			return this.visualization.getBlockThemeConfigContent(visualizationsConfig).order || [];
		}
		return [];
	}

	get allAvailableColors() {
		const colors: string[] = [];
		const allThemeColors = this.themeConfig.colors || [];
		this.allAvailableColorsIndexes.forEach((idx: number) => {
			const color = allThemeColors[idx];
			if (color) {
				colors.push(color);
			}
		});
		return colors;
	}

	private _allColorOptions: ColorOption[] = [];

	@cached
	get allColorOptions(): ColorOption[] {
		const allThemeColors = this.themeConfig.colors || [];
		const allColorOptions = this.allAvailableColors.map((hex: string) => {
			return {
				id: allThemeColors.indexOf(hex), // must be the index of the actual color in theme, not block-specific
				hex,
			};
		});
		if (this._allColorOptions !== allColorOptions) {
			// some themes have less colors than others
			next(this, this.checkBlockColorValidity);
			this._allColorOptions = allColorOptions;
		}
		return allColorOptions;
	}

	@cached
	get customColorClassMap() {
		// hex: string, e.g. `-custom-color-1` that also goes into series/point and is added to highcharts-* classes just like theme color index
		const customColorClassMap: CustomColorMap = {};

		if (this.visualization.canChangeColorsForSpecificMetrics) {
			const customColors: string[] = [];
			this.block.colors.forEach((cs) => {
				const color = cs.color;
				if (color && typeof color === 'string') {
					customColors.push(color);
				}
			});
			customColors.forEach((hex, index) => {
				customColorClassMap[hex] = `custom-color-${index}`;
			});
		}

		return customColorClassMap;
	}

	getColorForDataSettings(
		dataSelectId: number,
		dimension?: string,
		unit?: string,
	): number | string | null {
		const colorSetting = this.getColorSettingForDataSettings(dataSelectId, dimension, unit);
		return colorSetting ? colorSetting.color : null;
	}

	setColorForDataSettings(
		color: number | string | null,
		dataSelectId: number,
		dimension?: string,
		unit?: string,
	): void {
		let isChanged = false;

		const colorSettingInOriginal = this.getColorSettingForDataSettings(
			dataSelectId,
			dimension,
			unit,
		);

		// work on a copy bcos of buffered update (isDirty fails)
		let blockColorsCopy = copyObject(this.block.colors);

		let colorSetting: ColorSetting | undefined;
		if (colorSettingInOriginal) {
			colorSetting = blockColorsCopy.find((cs) => {
				return (
					cs.dataSelectId === colorSettingInOriginal.dataSelectId &&
					cs.dimension === colorSettingInOriginal.dimension &&
					cs.unit === colorSettingInOriginal.unit
				);
			});
		}

		if (typeof color === 'number' || typeof color === 'string') {
			if (colorSetting) {
				colorSetting.color = color;
				isChanged = true;
			} else if (dataSelectId) {
				const config: ColorSetting = { dataSelectId: dataSelectId, color };
				if (dimension) {
					config.dimension = dimension;
				}
				blockColorsCopy.push(config);
				isChanged = true;
			}
		} else {
			if (colorSetting) {
				blockColorsCopy = blockColorsCopy.filter((cs) => cs !== colorSetting);
				isChanged = true;
			}
		}

		if (isChanged) {
			// force update of array
			this.block.colors = blockColorsCopy;
			if (this.isAutosaveOn) {
				once(this, this.saveBlock);
			}
		}
	}

	getUsedColors(forVisualizationIndex = 0) {
		const dataSelectIdsForColorGroup =
			this.visualization
				.getDataSelectsByColorGroups()
				[forVisualizationIndex]?.map((ds) => ds.numericId) || [];

		const colors: (number | string)[] = [];
		this.block.colors.forEach((cs) => {
			if (dataSelectIdsForColorGroup.includes(cs.dataSelectId)) {
				colors.push(cs.color);
			}
		});

		return colors;
	}

	getUsedColorsOnDataSelect(dataSelect: DataSelect) {
		// including the ones on attributes
		const colors: (number | string)[] = [];
		const dataSelectId = dataSelect.numericId;
		if (dataSelectId) {
			this.block.colors.forEach((cs) => {
				if (dataSelectId === cs.dataSelectId) {
					colors.push(cs.color);
				}
			});
		}
		return colors;
	}

	checkBlockColorValidity(removeAllAttributeColors = false) {
		const allColorsCount = this.allAvailableColors.length,
			toRemove: ColorSetting[] = [];

		this.block.colors.forEach((cs) => {
			if (typeof cs.color === 'number' && cs.color >= allColorsCount) {
				toRemove.push(cs);
			}
		});

		// if two have the same color - must be diff visualization on ds itself
		const dataSelectsByColorGroups = this.visualization.getDataSelectsByColorGroups();

		dataSelectsByColorGroups.forEach((dsColorGroup) => {
			const once: number[] = [],
				dsIds = dsColorGroup.map((ds) => ds.numericId);
			this.block.colors.forEach((cs) => {
				// only validate theme colors (numbers)
				if (dsIds.includes(cs.dataSelectId)) {
					const color = cs.color;
					if (typeof color === 'number') {
						if (once.includes(color)) {
							toRemove.push(cs);
						} else {
							once.push(color);
						}
					}
				}
			});
		});

		// cleanup color settings for changed dataselects (e.g. cleaned up attributes)
		// or switched to visualization that does not support attribute colors
		const dataSelects = this.dataSelectsShown;
		this.block.colors.forEach((cs) => {
			let remove = false;
			const ds = dataSelects.find((ds) => ds.numericId === cs.dataSelectId);
			if (ds) {
				const usedAttributeNames = ds.metricSetup.attributeNamesFromBasicFilters;
				if (cs.dimension) {
					if (!usedAttributeNames.includes(cs.dimension) || removeAllAttributeColors) {
						remove = true;
					}
				}
			} else {
				remove = true;
			}
			if (remove) {
				toRemove.push(cs);
			}
		});

		toRemove.forEach((cs) => {
			this.setColorForDataSettings(null, cs.dataSelectId, cs.dimension, cs.unit);
		});
	}

	getColorForMetricSelectionData(metricSelectionData: MetricSelectionData, withFallback = true) {
		const dataSelectId = metricSelectionData.primaryDsId || metricSelectionData.dsId, // colors are set on primary
			unit = metricSelectionData.unit || undefined,
			dimensionValues = Object.values(metricSelectionData.dimensionValues),
			firstDimensionValue = dimensionValues[0];
		let colorSetting = this.getColorSettingForDataSettings(
			dataSelectId,
			firstDimensionValue,
			unit,
		);
		if (!colorSetting && withFallback && firstDimensionValue) {
			// fallback to metric's color
			colorSetting = this.getColorSettingForDataSettings(dataSelectId);
		}
		return colorSetting ? colorSetting.color : null;
	}

	private getColorSettingForDataSettings(
		dataSelectId: number,
		dimension?: string,
		_unit?: string,
	): ColorSetting | undefined {
		// we support per-DS+attribute colors, ignore unit
		return this.block.colors.find((cs) => {
			return cs.dataSelectId === dataSelectId && cs.dimension === dimension;
			// && cs.unit === unit;
		});
	}

	// ------ VISUALIZATION SPECIFICS ------

	get visualizationType(): BlockVisualization {
		return this.block.visualization;
	}

	set visualizationType(type: BlockVisualization) {
		this.block.visualization = type;
	}

	get hasBlockWideDataLoader(): boolean {
		// blockwide data loader is only for those that do not show
		// separate loaders at every item (listed below)
		const hasLoadersOnSpecificDataItems = [BlockVisualization.NUMBER, BlockVisualization.TABLE];
		return !hasLoadersOnSpecificDataItems.includes(this.visualizationType);
	}

	get isBlockWideDataLoaderOnlyFromSeries(): boolean {
		// where the main loader on block is shown only for the chart
		// and the number loaders are shown on numbers themselves
		// if false, all statusObjects are taken into account for block-wide loader
		return [BlockVisualization.LINE_CHART, BlockVisualization.BAR_CHART].includes(
			this.visualizationType,
		);
	}

	recreateVisualization(isFirstInit = false): void {
		const newVisualization = this.getVisualizationModelForVisualizationType(
			this.visualizationType,
		);
		if (!isFirstInit) {
			const oldVisualization = this.visualization;
			if (oldVisualization) {
				newVisualization.rendererReferenceCount = oldVisualization.rendererReferenceCount;
			}
		}
		this.visualization = newVisualization;
	}

	// ------ VISUALIZATION SWITCHING ------

	changeVisualizationWithPossibleConfirm(
		toVisualizationType: BlockVisualization,
	): ChangeVisualizationWithPossibleConfirmFnReturnType {
		// if needs confirm, returns fn to execute on confirm
		// otherwise executes vis switch and returns null

		if (this.visualizationType === toVisualizationType) {
			return null;
		}

		const onConfirm = () => {
			void this.changeVisualization(toVisualizationType);
		};

		const mustConfirm = this.mustConfirmVisualizationChange(toVisualizationType);

		if (mustConfirm) {
			return onConfirm;
		} else {
			onConfirm();
			return null;
		}
	}

	changeVisualization(visualizationType: BlockVisualization) {
		const visualizationFrom = this.visualization,
			visualizationTo = this.getVisualizationModelForVisualizationType(visualizationType);

		this.loggerService.logToConsole(
			`[Vis change] from ${visualizationFrom.visualizationName} to ${visualizationTo.visualizationName}`,
		);

		// group up all data selects that will be deleted
		const dataSelectsToBeDeleted: DataSelect[] = [];

		// get obsolete visualization-specific data selects (f.ex numbers for chart)
		{
			const excessDs = visualizationFrom.getExcessDataSelectsForSwitchTo(visualizationTo);
			dataSelectsToBeDeleted.push(...excessDs);
			if (dataSelectsToBeDeleted.length) {
				const ids = dataSelectsToBeDeleted.map((item) => item.id).toString();
				this.loggerService.logToConsole(
					`[Vis change] Deleting excess visualization-specific DS ${ids}`,
				);
			}
		}

		// get leftovers, if recordLimit is reached
		{
			const excessDs = this.getExcessDataSelectsForSwitchTo(visualizationTo);
			excessDs.forEach((ds) => {
				if (!dataSelectsToBeDeleted.includes(ds)) {
					dataSelectsToBeDeleted.push(ds);
				}
			});
			if (excessDs.length) {
				const ids = excessDs.map((item) => item.id).toString();
				this.loggerService.logToConsole(`[Vis change] Deleting excess DS ${ids}`);
			}
		}

		// if primary is being deleted, don't send requests for their secondaries
		// bcos secondaries will be removed automatically by BE
		dataSelectsToBeDeleted.forEach((ds) => {
			const isPrimaryBeingDeleted =
				!ds.isPrimary &&
				!!dataSelectsToBeDeleted.find((d) => d.numericId === ds.updateFrom);
			if (!isPrimaryBeingDeleted) {
				this.deleteDataSelect(ds);
			}
		});

		// update date ranges, if necessary
		// checking the actual dateRange instead of "what current visualization should be"
		// to also safely "migrate" any incorrect date-range setups we might hae
		const hasGranulation = visualizationTo.hasGranulation,
			currentHasGranulation = !this.limitations.dateRange?.granulationIsWholeRange;
		if (currentHasGranulation !== hasGranulation) {
			this.loggerService.logToConsole(
				`[Vis change] Switch from ${hasGranulation ? 'number to series' : 'series to number'}`,
			);
			this.updateIsNumberOnLimitations(!hasGranulation);
			this.block.discardSampleData();
		}

		// cleanup compares, if necessary
		if (!visualizationTo.allowMultipleComparesOnPrimaryDs) {
			const leaveFirstCompare = visualizationTo.showCompareOnPrimaryDs;
			this.loggerService.logToConsole(
				`[Vis change] Remove all compares ${leaveFirstCompare ? 'except first' : ''}`,
			);
			this.primaryDataSelects.forEach((ds) => {
				ds.removeCompares(!leaveFirstCompare);
			});
		}

		// remove skipAutoUpdate on primary ds, if the target visualization does not support such
		if (!visualizationTo.allowAutoUpdateOnPrimary) {
			this.removeSkipAutoUpdateOnPrimaryDataSelects();
		}

		if (!visualizationTo.showStackedChoiceOnDs) {
			this.removeIsStackedOnPrimaryDataSelects();
		}

		visualizationFrom.preCleanupForSwitchTo(visualizationTo);
		visualizationTo.updateForSwitchFrom(visualizationFrom);

		this.visualizationType = visualizationType;
		this.visualization = visualizationTo;

		const block = this.block;

		// set correct record limit
		block.recordLimit = visualizationTo.recordLimit;

		// cleanup sort options
		if (!visualizationTo.showSortOptions) {
			this.loggerService.logToConsole('[Vis change] Remove sort settings');
			block.sortField = null;
			block.sortOrder = null;
		}

		// cleanup showOther
		if (!visualizationTo.allowOtherGrouping) {
			this.loggerService.logToConsole('[Vis change] Remove showOther');
			block.showOther = false;
		}

		if (
			visualizationTo.showLegendPosition &&
			block.legendPosition !== ChartLegendPosition.OFF
		) {
			this.loggerService.logToConsole('[Vis change] Set legend position to default');
			block.legendPosition = visualizationTo.isPie
				? ChartLegendPosition.OUTSIDE
				: ChartLegendPosition.BOTTOM;
		}

		// remove colors from dimensions if they're not supported
		if (
			visualizationFrom.canChangeColorsForSpecificAttributes &&
			!visualizationTo.canChangeColorsForSpecificAttributes
		) {
			this.checkBlockColorValidity(true);
		}

		this.setTableColumnTypeBasedOnMetric();

		// here, don't check for isAutosaveOn, visualization change is always saved immediately
		if (this.isTemporaryBlock) {
			return Promise.resolve();
		} else {
			// here, don't check for isAutosaveOn, visualization change is always saved immediately

			return this.saveEverything().then(() => {
				this.mmBlocksService.checkToMarkBlockVisualizationUsed(block);
			});
		}
	}

	getCompatibleVisualizationsForCurrentVisualization(): BlockVisualization[] {
		// can switch between any of the visualizations in the group
		const groups = [
			[BlockVisualization.LINE_CHART, BlockVisualization.BAR_CHART],
			[
				BlockVisualization.NUMBER,
				BlockVisualization.PIE,
				BlockVisualization.TABLE,
				BlockVisualization.AI_SUMMARY,
			],
			[BlockVisualization.PIPELINE, BlockVisualization.FUNNEL],
			[
				BlockVisualization.PROGRESS,
				BlockVisualization.GAUGE,
				BlockVisualization.RADIAL_PROGRESS,
			],
		];

		const current = this.visualizationType;
		return (
			groups.find((g) => {
				return g.includes(current);
			}) || []
		);
	}

	mustConfirmVisualizationChange(toVisualizationType: BlockVisualization): boolean {
		const visualizationTo = this.getVisualizationModelForVisualizationType(toVisualizationType);

		const excessDataSelectsToBeDeleted = this.getExcessDataSelectsForSwitchTo(visualizationTo);

		return excessDataSelectsToBeDeleted.length > 0;
	}

	removeSkipAutoUpdateOnPrimaryDataSelects() {
		this.primaryDataSelects.forEach((ds) => {
			if (ds.skipAutoUpdate) {
				ds.skipAutoUpdate = false;
			}
		});
	}

	private removeIsStackedOnPrimaryDataSelects() {
		this.primaryDataSelects.forEach((ds) => {
			if (ds.isStacked) {
				this.changeIsStacked(ds, false, true);
			}
		});
	}

	private getExcessDataSelectsForSwitchTo(visualizationTo: AnyVisualization) {
		return this.dataSelectsShown.slice(visualizationTo.maxPrimaryMainDataSelectsAllowed);
	}

	setTableColumnTypeBasedOnMetric(): void {
		if (this.visualization.isTable) {
			const dataSelects = this.dataSelectsShown;

			const anyAttributed = dataSelects.some((ds) => {
				return ds.metricSetup.isDimensionSelected;
			});
			if (anyAttributed) {
				next(() => {
					this.block.tableColumnType = TableColumnType.METRICS;
				});
			}
		}
	}

	// ------ LIMITATIONS HANDLING  ------

	private _limitations: MetricLimitations | null = null;

	@cached
	get limitations(): MetricLimitations {
		// re-creates limitations on metrics or (primary) dateRange change
		// this is primarily needed for "outside" changes (e.g. pusher updates)
		// and updates in another instance of blockSetup (e.g. block in preview when designer sidebar is updated)

		let cachedLimitations = this._limitations;

		if (cachedLimitations) {
			if (
				!equal(cachedLimitations.metrics, this.metricDataArray) ||
				(this.firstShownDataSelect &&
					!cachedLimitations.dateRange?.isEqual(this.firstShownDataSelect?.dateRange))
			) {
				cachedLimitations = null;
			}
		}

		if (!cachedLimitations) {
			cachedLimitations = this.createLimitations();
			this._limitations = cachedLimitations;
		}

		return cachedLimitations;
	}

	@cached
	get metricSetupDataArray() {
		const metricData: DSSetup.MetricSetupData[] = [];
		this.dataSelectsShown.forEach((ds) => {
			const data = ds.metricSetup.metricSetupData;
			if (data) {
				metricData.push(data);
			}
		});
		return metricData;
	}

	@cached
	get metricDataArray() {
		const metricData: DSSetup.MetricData[] = [];
		this.dataSelectsShown.forEach((ds) => {
			const data = ds.metricSetup.metricData;
			if (data) {
				metricData.push(data);
			}
		});
		return metricData;
	}

	private createLimitations(): MetricLimitations {
		const manualLimitations = this.isTemporaryBlock;

		// set daterange
		let dateRange;

		const primaryDateRange = this.firstShownDataSelect?.limitations.dateRange;

		if (primaryDateRange) {
			dateRange = primaryDateRange.createCopy();
		} else {
			dateRange = this.createDefaultDateRange();
		}

		return new MetricLimitations({
			isNumber: !this.visualization.hasGranulation,
			metrics: this.metricDataArray,
			dateRange,
			manualLimitations,
		});
	}

	isDefaultDateRange() {
		const dateRangeFormula = this.limitations.dateRange?.formula;
		return dateRangeFormula && this.createDefaultDateRange().isEqual(dateRangeFormula);
	}

	private createDefaultDateRange() {
		// last 30 days
		return DateRange.createLastX(
			30,
			TimeUnit.DAY,
			this.visualization.hasGranulation ? GranulationTypeId.DAILY : GranulationTypeId.TOTAL,
		);
	}

	changeFullDateRange(dateRange: DateRange): void {
		// changes the full date range, incl granulation as given
		// called when changing DR on blocks in designer or on reports

		const limitations = this.limitations;
		if (limitations.dateRange?.isEqual(dateRange.formula)) {
			return;
		}

		limitations.updateDateRangeFromFormula(dateRange.formula);

		if (limitations.dateRange) {
			void this.updateDataSelectsFromPrimaryDateRange(limitations.dateRange);
		}
	}

	changeDateRange(dateRange: DateRange): void {
		// used for just range changes, and sets granulation / cumulative to default

		const limitations = this.limitations;
		if (limitations.dateRange?.isEqual(dateRange.formula)) {
			return;
		}

		limitations.updateDateRangeFromOnlyRangeFormula(dateRange.rangeFormula);
		if (this.visualization.hasGranulation) {
			limitations.selectOptimalValidGranulation();
		}

		if (limitations.dateRange) {
			void this.updateDataSelectsFromPrimaryDateRange(limitations.dateRange);
		}
	}

	changeGranulation(granulationId: GranulationTypeId): void {
		const dateRange = this.limitations.dateRange;
		if (dateRange) {
			dateRange.updateGranulationFromTypeId(granulationId);
			void this.updateDataSelectsFromPrimaryDateRange(dateRange);
		}
	}

	changeCumulative(cumulative: boolean): void {
		const dateRange = this.limitations.dateRange;
		if (dateRange) {
			dateRange.granulationCumulative = cumulative;
			void this.updateDataSelectsFromPrimaryDateRange(dateRange);
		}
	}

	updateIsNumberOnLimitations(isNumber: boolean): void {
		const limitations = this.limitations,
			oldFormula = limitations.dateRange?.formula;
		limitations.isNumber = isNumber;
		limitations.selectOptimalValidGranulation();
		if (limitations.dateRange && oldFormula && !limitations.dateRange.isEqual(oldFormula)) {
			void this.updateDataSelectsFromPrimaryDateRange(limitations.dateRange);
		}
	}

	updateDataSelectsFromPrimaryDateRange(dateRange: DateRange) {
		const dataSelects = this.dataSelects;
		if (!dataSelects.length) {
			// no point, the date range is not saved on block itself
			return Promise.resolve();
		}

		// use batch block date-range change endpoint
		// do not call it at the same time block edit due to race condition issues
		const promise = new Promise((resolve) => {
			void this.savePromise.then(() => {
				// immediately update all data selects (will not be returned from BE)
				dataSelects.forEach((ds) => {
					if (!ds.skipAutoUpdate) {
						const wasDsCleanInitially = !ds.isDirty,
							isNumberOnChart =
								!ds.isPrimary &&
								ds.limitations.isNumber &&
								(this.visualization.isLineChart || this.visualization.isBarChart),
							isChartOnNumber =
								!ds.isPrimary &&
								!ds.limitations.isNumber &&
								this.visualization.isNumber,
							onlyRange = isNumberOnChart || isChartOnNumber; // secondaries might have diff granulation type
						ds.updateDateRange(dateRange, onlyRange);
						ds.applyBufferedChanges(['dateRange', 'compares']);
						if (wasDsCleanInitially) {
							ds.markAsClean();
						}
					}
				});

				const block = this.block;

				if (this.isTemporaryBlock || !this.isAutosaveOn) {
					resolve(null);
				} else {
					this.mmBlocksService.addBlockIdToRequestedSamples(block.id!);

					void this.mmBlocksService.batchChangeDateRange(block, dateRange).then(resolve);
				}

				// immediately update also the block (will be returned from BE, but we play it safe)
				// otherwise e.g. series data might be shown on number-based blocks until /date-range response comes in
				// do not markAsClean bcos we might have queued visualization changes which will be skipped otherwise
				block.model.dateRange = dateRange.formula;
			});
		});

		this.savePromise = promise;

		return promise;
	}

	// ------ UPDATING SPECIFIC DATASELECTS ------

	changeMetric(dataSelect: DataSelect, metricData: DSSetup.MetricData): void {
		dataSelect.updateMetricData(metricData);

		this.visualization.onChangeMetricInDataSelect(dataSelect);

		this.scheduleCheckGoalCompareValidity(dataSelect);

		if (this.isAutosaveOn) {
			this.saveDataSelect(dataSelect);
		}
		this.checkBlockColorValidity();
		this.limitations.changeMetrics(this.metricDataArray);

		if (this.isAutosaveOn && this.block.name === this.mmBlocksService.DEFAULT_NAME) {
			const isMainDataSelect =
				dataSelect.id === this.firstShownDataSelect?.id &&
				this.dataSelectsShown.length === 1;
			const metricName = dataSelect.metricSetup.metric?.name;
			if (isMainDataSelect && metricName) {
				this.block.name = metricName;
				once(this, this.saveBlock);
			}
		}
	}

	changeDimensions(dataSelect: DataSelect, dimensions: string[]): void {
		dataSelect.updateDimensions(dimensions);
		this.checkBlockColorValidity();
		if (this.isAutosaveOn) {
			this.saveDataSelect(dataSelect);
		}
	}

	validateDimensionsAfterMetricEdit(dataSelect: DataSelect) {
		// used after editing custom metric. reset dimension if invalid is chosen
		const metric = dataSelect.metricSetup.metric;
		if (!metric) {
			return;
		}

		// updating dimensions on dataSelect model
		const selectedDimensionOnDataSelectModel = dataSelect.metricSetupDimensions[0] || null;
		let isInvalid = false;
		if (selectedDimensionOnDataSelectModel) {
			if (!metric.dimensionsAvailableKeys.includes(selectedDimensionOnDataSelectModel)) {
				isInvalid = true;
			}
		} else if (metric.mustSelectDimension) {
			// select first
			isInvalid = true;
		}

		if (isInvalid) {
			const firstValid = metric.dimensionsAvailableKeys[0] || null;
			const validArray = firstValid ? [firstValid] : [];
			this.changeDimensions(dataSelect, validArray);
		}

		// save even in case isInvalid is false, we force save if the record metricSetupDimensions does not match the model
		// this can happen after editing metric to not support the dimension that is already selected
		// and mixed with autoselectedDimension it can happen that isInvalid is false (model was already updated (see afterMetricSet))
		// and all we need to do is call save
		if (this.isAutosaveOn && dataSelect.dirtyProperties.includes('metricSetupDimensions')) {
			this.saveDataSelect(dataSelect);
		}
	}

	changeAttributes(dataSelect: DataSelect, attributes: string[]): void {
		// only use for features that don't support advanced filtering
		dataSelect.updateAttributes(attributes);
		this.checkBlockColorValidity();
		this.scheduleCheckGoalCompareValidity(dataSelect);
		if (this.isAutosaveOn) {
			this.saveDataSelect(dataSelect);
		}
	}

	changeUnit(dataSelect: DataSelect, unit: string): void {
		dataSelect.updateUnit(unit);
		this.scheduleCheckGoalCompareValidity(dataSelect);
		if (this.isAutosaveOn) {
			this.saveDataSelect(dataSelect);
		}
	}

	changeFilters(dataSelect: DataSelect, filters: DSSetup.MetricSetupFiltersData | null): void {
		dataSelect.updateFilters(filters);
		this.checkBlockColorValidity();
		this.scheduleCheckGoalCompareValidity(dataSelect);
		if (this.isAutosaveOn) {
			this.saveDataSelect(dataSelect);
		}
	}

	changeCompare(dataSelect: DataSelect, oldCompare: DsCompare, newCompare: DsCompare) {
		// assigning here bcos oldCompare will get updated with ds.changeCompare
		const isSwitchingFromNone = oldCompare.isNone,
			isSwitchingFromGoal = oldCompare.isGoal;
		const goalLineForOldCompare = isSwitchingFromGoal
			? this.getAdditionalGoalLineForCompare(dataSelect, oldCompare)
			: null;

		dataSelect.changeCompare(oldCompare, newCompare);

		if (!this.isAutosaveOn) {
			return;
		}

		this.saveDataSelect(dataSelect);

		if (this.visualization.showAdditionalLines) {
			// line and barchart additional lines smart logic

			const visualization = this.visualization as
					VisualizationLineChart | VisualizationBarChart,
				allCompares = dataSelect.limitations.compareCMs.filter(
					(compare) => !compare.isNone,
				),
				compareLineDs: DataSelect | null =
					visualization
						.getSecondarySeriesForChartDs(dataSelect)
						.find((ds) => ds.type === DataSelectType.COMPARE) || null;

			// handling of "Compare" additional line
			const shouldHaveCompareLine = !newCompare.isNone && !newCompare.isGoal;
			if (shouldHaveCompareLine) {
				if (!isSwitchingFromNone && compareLineDs) {
					// update compare line from first compare
					if (allCompares.indexOf(oldCompare) === 0) {
						// UPDATE
						const primaryDsDateRange = dataSelect.limitations.dateRange!;

						const compareFormula = newCompare.getFormula()!;
						if (newCompare.isOtherMetric) {
							compareLineDs.updateMetricSetupData(
								newCompare.metricSetup.metricSetupData,
							);
							compareLineDs.updateDateRange(primaryDsDateRange); // matches with primary
						} else {
							const compareDateRangeFormula = compareFormula?.dateRange;
							const dsDateRangeFormula: DateRangeInterface.FormulaInterface =
								Object.assign({}, primaryDsDateRange.formula);
							if (compareDateRangeFormula) {
								if (compareDateRangeFormula.range) {
									dsDateRangeFormula.range = compareDateRangeFormula.range;
								}
								if (compareDateRangeFormula.offset) {
									dsDateRangeFormula.offset = compareDateRangeFormula.offset;
								}
								// updating directly bcos of "offset" not supported in date-range model
								compareLineDs.dateRange = dsDateRangeFormula;
							}
						}

						this.saveDataSelect(compareLineDs);
					}
				} else if (allCompares.length === 1) {
					// add compare line from first compare
					this.addSecondaryDataSelectForDataSelect(dataSelect, DataSelectType.COMPARE);
				}
			} else if (!allCompares.length && newCompare.isNone) {
				// remove additional compare line when just deleted the last compare
				if (compareLineDs) {
					this.deleteDataSelect(compareLineDs);
				}
			}

			// Handling of "Goal" additional lines
			const goalLinesDs = visualization
				.getSecondarySeriesForChartDs(dataSelect)
				.filter((ds) => ds.type === DataSelectType.GOAL);

			if (isSwitchingFromGoal) {
				if (goalLineForOldCompare) {
					if (newCompare.isGoal) {
						// update goal line
						goalLineForOldCompare.updateMetricData(newCompare.metricSetup.metricData);
						this.saveDataSelect(goalLineForOldCompare);
					} else {
						// remove goal line
						this.deleteDataSelect(goalLineForOldCompare);
					}
				}
			} else if (newCompare.isGoal) {
				// we only want to add goal lines if the toggler is on
				// it is considered on if there are goal lines already, or we are adding first goal compare now
				const isGoalAdditionalLinesOn =
					goalLinesDs.length > 0 ||
					allCompares.filter((item) => item.isGoal).length === 1;
				if (isGoalAdditionalLinesOn) {
					this.createGoalLineForCompare(dataSelect, newCompare);
				}
			}
		}

		if (this.visualization.canShowAndDisableNumberOnMetric) {
			// updating compares on the secondary ms (number)
			const visualization = this.visualization as
				VisualizationLineChart | VisualizationBarChart;
			const numberDs = visualization.getNumberForChartDs(dataSelect);
			if (numberDs && !numberDs.skipAutoUpdate) {
				// copy the compares from main, but update / remove ids
				const compares = copyObject(dataSelect.compares);
				compares.forEach((c: DSSetup.Compare, idx: number) => {
					const existingCompareId = numberDs.compares[idx]?.id;
					if (existingCompareId) {
						c.id = existingCompareId;
					} else {
						delete c.id;
					}
				});
				numberDs.compares = compares;
				this.saveDataSelect(numberDs);
			}
		}
	}

	changeIsStacked(dataSelect: DataSelect, isStacked: boolean, skipSave = false): void {
		dataSelect.isStacked = isStacked;
		if (!skipSave) {
			this.saveDataSelect(dataSelect);
		}

		if (this.visualization.showAdditionalLines) {
			const secondaryDataSelects = this.getSecondaryDataSelectsForDataSelect(dataSelect);
			const dataSelectToUpdate = secondaryDataSelects.find(
				(ds) => ds.type === DataSelectType.COMPARE,
			);

			if (dataSelectToUpdate) {
				dataSelectToUpdate.isStacked = isStacked;

				if (!skipSave) {
					this.saveDataSelect(dataSelectToUpdate);
				}
			}
		}
	}

	saveDataSelect(dataSelect: DataSelect): void {
		if (dataSelect.blockId) {
			this.mmBlocksService.addBlockIdToRequestedSamples(String(dataSelect.blockId));
		}

		void this.addToSaveQueue(dataSelect).finally(() => {
			this.mmBlocksService.removeBlockIdFromRequestedSamples(String(dataSelect.blockId));
		});
	}

	deleteDataSelect(dataSelect: DataSelect): void {
		// delete colors for this dataSelect
		this.block.colors.forEach((cs) => {
			if (cs.dataSelectId === dataSelect.numericId) {
				this.setColorForDataSettings(null, cs.dataSelectId, cs.dimension, cs.unit);
			}
		});

		const secondaryDataSelects = this.getSecondaryDataSelectsForDataSelect(dataSelect),
			updateFrom = dataSelect.numericId;
		void this.addToDeleteQueue(dataSelect).then(() => {
			secondaryDataSelects.forEach((secondaryDataSelect) => {
				const dataSelectRecord = secondaryDataSelect.model;

				if (dataSelectRecord && updateFrom && dataSelectRecord.updateFrom === updateFrom) {
					// After deleting main dataSelect we need just to unload
					// belonging records (according to updateFrom), since other
					// dataSelects are removed via BE
					dataSelectRecord.unloadRecord();
				}
			});
		});

		this.visualization.onDeleteDataSelect(dataSelect);
	}

	createPrimaryMainDataSelect(
		props: DataSelectProps = {},
		skipCreateDefaultCompare = false,
	): DataSelect {
		props.order = this.getNextOrder();

		if (!props.dateRange) {
			props.dateRange = this.limitations.dateRange?.formula;
		}

		const isFirst = !this.dataSelects.length;

		// without save (yet) bcos we might add props in onCreatePrimaryMainDataSelect
		const dataSelect = this.createDataSelectWithoutSave(props);

		// add compare with previous on the primary dataselects, if visualization allows it
		if (
			!skipCreateDefaultCompare &&
			this.visualization.showCompareOnPrimaryDs &&
			!dataSelect.compares.length
		) {
			const dateRange = dataSelect.limitations.dateRange;
			if (dateRange && !dateRange.isAllTime && !dateRange.isSince) {
				const newCompare = DsCompare.createFromFormula(null);
				newCompare.updateFromTypeId(DsCompare.customPreviousId, dateRange);
				dataSelect.changeCompare(dataSelect.limitations.compareCMs[0]!, newCompare);
			}
		}

		this.visualization.onCreatePrimaryMainDataSelect(dataSelect);

		if (this.isAutosaveOn) {
			this.saveDataSelect(dataSelect);

			// add compare line, if supported
			this.checkToAddAdditionalLines(dataSelect);
		}

		// when adding the first dataSelect, update also dateRange on the block (w/o save)
		// bcos BE won't be sending block in this case (to avoid race conditions)
		if (isFirst) {
			const block = this.block;
			const wasDsCleanInitially = !block.isDirty;
			block.dateRange = copyObject(dataSelect.dateRange);
			block.applyBufferedChanges(['dateRange']);
			if (wasDsCleanInitially) {
				block.markAsClean();
			}
		}

		return dataSelect;
	}

	checkToAddAdditionalLines(dataSelect: DataSelect) {
		if (this.visualization.showAdditionalLines) {
			void this.afterSaves().then(() => {
				next(() => {
					const compareDs = this.addSecondaryDataSelectForDataSelect(
						dataSelect,
						DataSelectType.COMPARE,
					);
					if (compareDs) {
						this.saveDataSelect(compareDs);
					}
				});
			});
		}
	}

	addGoalCompareAndDataSelect(
		dataSelect: DataSelect,
		goalKeyLong: string,
		spaceAccessId: number | null,
		extraDsProps?: DataSelectProps,
	): DataSelect | null {
		const emptyCompare = DsCompare.createFromFormula(null);
		dataSelect.limitations.addCompare(emptyCompare);

		const goalCompare = DsCompare.createFromFormula({
			type: CompareType.GOAL,
			metricSetup: { key: goalKeyLong, spaceAccessId },
		});
		this.changeCompare(dataSelect, emptyCompare, goalCompare);

		return this.addSecondaryDataSelectForDataSelect(dataSelect, DataSelectType.GOAL, {
			metricSetupKey: goalKeyLong,
			metricSetupSpaceAccessId: spaceAccessId,
			// a goal metric has no dimensions or filters; don't inherit the tracked metric's breakdown
			metricSetupDimensions: [],
			metricSetupFilters: null,
			...extraDsProps,
		});
	}

	createGoalLineForCompare(dataSelect: DataSelect, compare: DsCompare) {
		const dsData: DataSelectProps = {
			metricSetupKey: compare.metricSetup.key,
			metricSetupSpaceAccessId: compare.metricSetup.spaceAccessId,
			metricSetupDimensions: compare.metricSetup.dimensions, // empty but we want to overwrite defaults
			metricSetupFilters: null, // empty but we want to overwrite defaults
		};

		return this.addSecondaryDataSelectForDataSelect(dataSelect, DataSelectType.GOAL, dsData);
	}

	private scheduleCheckGoalCompareValidity(dataSelect: DataSelect) {
		once(this, this.checkGoalCompareValidity, dataSelect);
	}

	private checkGoalCompareValidity(dataSelect: DataSelect) {
		if (!dataSelect.isPrimaryMain) {
			return;
		}

		const validGoals = this.mmGoalsService.getValidGoalsForMetricSetup(dataSelect.metricSetup);
		const validGoalKeys = validGoals.map((goal) => goal.goalKeyLong);
		const goalCompares = dataSelect.limitations.compareCMs.filter((compare) => compare.isGoal);

		goalCompares.forEach((compare: DsCompare, idx: number) => {
			const goalKeyLong = compare.metricSetup.key; // goal key is unique

			if (!goalKeyLong || !validGoalKeys.includes(goalKeyLong)) {
				// if there is only one valid goal, select it automatically
				// delete the rest of the goal compares (unless goal compare is obligatory (gauge))
				if (!idx && (validGoalKeys.length === 1 || this.visualization.goalAlwaysVisible)) {
					const existingValidCompare = goalCompares.find((compare) => {
						return compare.metricSetup.key === validGoalKeys[0];
					});
					const metricSetup = dataSelect.metricSetup.metricSetupWithoutFiltersData;
					if (!existingValidCompare && metricSetup) {
						const newCompare = DsCompare.createFromFormula({
							type: CompareType.GOAL,
							metricSetup: {
								key: validGoalKeys[0]!,
								spaceAccessId: metricSetup.spaceAccessId,
							},
						});
						this.changeCompare(dataSelect, compare, newCompare);
						return;
					}
				}
				this.changeCompare(dataSelect, compare, DsCompare.createFromFormula(null));
			}
		});
	}

	private getAdditionalGoalLineForCompare(dataSelect: DataSelect, compare: DsCompare) {
		if (!this.visualization.showAdditionalLines) {
			return null;
		}

		const visualization = this.visualization as VisualizationLineChart | VisualizationBarChart;

		const goalLinesDs = visualization
			.getSecondarySeriesForChartDs(dataSelect)
			.filter((ds) => ds.type === DataSelectType.GOAL);

		const compareMetricData = compare.metricSetup.metricData;

		return goalLinesDs.find((ds) => {
			return (
				(!ds.metricSetupKey && !compareMetricData?.key) ||
				ds.metricSetupKey === compareMetricData?.key
			);
		});
	}

	private createDataSelectWithoutSave(props: DataSelectProps = {}): DataSelect {
		props.blockId = this.block.numericId;
		const ds = this.mmDataSelectsService.createNewModel(
			props as CreateRecordProperties<DataSelectRecord>,
		);
		return ds;
	}

	createDataSelect(props: DataSelectProps = {}): DataSelect {
		const ds = this.createDataSelectWithoutSave(props);
		if (this.isAutosaveOn) {
			this.saveDataSelect(ds);
		}
		return ds;
	}

	getTypeTextForDataSelect(dataSelect: DataSelect): string {
		return this.visualization.getTypeTextForDataSelect(dataSelect);
	}

	trackMetricDataEvent(event: TrackingEvent, dataSelects = this.dataSelects, origin?: string) {
		// next, to actually get the just-changed dateRange from limitations
		next(() => {
			const dateRange = this.limitations.dateRange;
			if (dateRange) {
				const originToTrack = origin || `datablock_settings_${this.block.trackingOrigin}`;
				const mkIdsTracked: string[] = [];
				dataSelects.forEach((ds) => {
					const metric = ds.metricSetup.metric,
						metricKey = metric?.id;
					if (metric && metricKey && !mkIdsTracked.includes(metricKey)) {
						mkIdsTracked.push(metricKey);
						this.mmMetricsService.trackMetricDataEvent(
							event,
							metric,
							dateRange,
							originToTrack,
						);
					}
				});
			}
		});
	}

	trackDataSelectMetrickeyChangeEvent(dataSelect: DataSelect) {
		const trackingData = Object.assign(
			{
				origin: this.block.trackingOrigin,
			},
			dataSelect.getMetricTrackingData(),
		);
		this.analyticsService.event(TrackingEvent.BLOCK_PROPERTIES_METRIC_SELECTED, trackingData);
	}

	trackBlockPropertySettingsChangeEvent(settingData: APIData.Tracking.Data) {
		const eventData = Object.assign(
			{
				visualization: this.block.visualizationName,
				origin: this.block.trackingOrigin,
			},
			settingData,
		);
		this.analyticsService.event(TrackingEvent.BLOCK_PROPERTIES_SETTINGS_CHANGED, eventData);
	}

	trackBlockPropertyMetricSettingsChangeEvent(
		dataSelect: DataSelect,
		settingData: APIData.Tracking.Data,
	) {
		const eventData = Object.assign(
			{
				visualization: this.block.visualizationName,
				origin: this.block.trackingOrigin,
			},
			settingData,
			this.getMetricDataForTracking(dataSelect),
		);
		this.analyticsService.event(
			TrackingEvent.BLOCK_PROPERTIES_METRIC_SETTINGS_CHANGED,
			eventData,
		);
	}

	getMetricDataForTracking(dataSelect: DataSelect) {
		const metricTrackingData = dataSelect.getMetricTrackingData();
		if (metricTrackingData.metrics.length) {
			return metricTrackingData;
		}
		return {};
	}

	saveBlock(): void {
		void this.addToSaveQueue(this.block);
	}

	saveEverything() {
		// save DS
		this.dataSelects.forEach((ds) => {
			this.saveDataSelect(ds);
		});

		// save the block
		this.saveBlock();

		return this.afterSaves();
	}

	afterSaves() {
		return this.savePromise;
	}

	doAfterNewBlockCreatedActivities() {
		return this.visualization.doAfterNewBlockCreatedActivities().then(() => {
			return this.saveEverything();
		});
	}

	// ------ SAVING ONE ENTITY AT A TIME LOGIC ------

	private savePromise: Promise<unknown> = Promise.resolve();
	private recordsInSaveQueue: (DataSelectRecord | BlockRecord)[] = [];

	private addToSaveQueue(model: Block | DataSelect) {
		const record = model.model;

		if (!model.isNew && !model.isDirty) {
			return this.savePromise;
		}

		// apply changes now, and force save later
		model.applyBufferedChanges();

		if (this.recordsInSaveQueue.includes(record)) {
			// already in queue, will send applied changes when requesting
			return this.savePromise;
		}

		if (this.isTemporaryBlock) {
			return Promise.resolve();
		} else {
			this.recordsInSaveQueue.push(record);
			const promise = new Promise((resolve) => {
				void this.savePromise.then(() => {
					// remove from queue as soon as we start request
					// so all changes while request is in progress get queued again
					const indexToRemove = this.recordsInSaveQueue.indexOf(record);
					this.recordsInSaveQueue.splice(indexToRemove, 1);
					void model.saveAndCatch(true).then(resolve).then(this.triggerChangeEvent);
				});
			});

			this.savePromise = promise;

			return promise;
		}
	}

	private addToDeleteQueue(model: DataSelect): Promise<unknown> {
		// no queue, delete DS immediately
		if (this.isTemporaryBlock) {
			return Promise.resolve();
		} else {
			return model.deleteAndCatch().then(this.triggerChangeEvent);
		}
	}

	// ------ EVENT HOOKS ------

	private registeredEvents: EventFunction[] = [];

	registerEvent(callbackFn: EventFunction) {
		// v simple, trigger on all changes / saves
		this.registeredEvents.push(callbackFn);
	}

	unregisterEvent(callbackFn: EventFunction) {
		this.registeredEvents = this.registeredEvents.filter((e) => e !== callbackFn);
	}

	@action
	private triggerChangeEvent() {
		this.registeredEvents.forEach((e) => {
			e();
		});
	}

	// ------ CREATING VISUALIZATION MODELS ------

	getMaxPrimaryMainDataSelectsAllowed(visualizationType: BlockVisualization) {
		return this.getVisualizationModelForVisualizationType(visualizationType)
			.maxPrimaryMainDataSelectsAllowed;
	}

	private getVisualizationModelForVisualizationType(visualizationType: BlockVisualization) {
		const b = this.block;
		switch (visualizationType) {
			case BlockVisualization.NUMBER:
				return new VisualizationNumber(b);
			case BlockVisualization.LINE_CHART:
				return new VisualizationLineChart(b);
			case BlockVisualization.BAR_CHART:
				return new VisualizationBarChart(b);
			case BlockVisualization.FUNNEL:
				return new VisualizationFunnel(b);
			case BlockVisualization.PIPELINE:
				return new VisualizationPipeline(b);
			case BlockVisualization.PROGRESS:
				return new VisualizationProgress(b);
			case BlockVisualization.TABLE:
				return new VisualizationTable(b);
			case BlockVisualization.PIE:
				return new VisualizationPie(b);
			case BlockVisualization.RADIAL_PROGRESS:
				return new VisualizationRadialProgress(b);
			case BlockVisualization.HORIZONTAL_BAR_CHART:
				return new VisualizationHorizontalBarChart(b);
			case BlockVisualization.DATA_STORY:
				return new VisualizationDataStory(b);
			case BlockVisualization.HEATMAP:
				return new VisualizationHeatmap(b);
			case BlockVisualization.BUBBLE:
				return new VisualizationBubble(b);
			case BlockVisualization.SPIDER:
				return new VisualizationSpider(b);
			case BlockVisualization.GAUGE:
				return new VisualizationGauge(b);
			case BlockVisualization.TEXT:
				return new VisualizationText(b);
			case BlockVisualization.IMAGE:
				return new VisualizationImage(b);
			case BlockVisualization.AI_SUMMARY:
				return new VisualizationAiSummary(b);
			default:
				return new VisualizationNumber(b);
		}
	}

	static getRecordLimitForVisualizationType(visualizationType: BlockVisualization) {
		switch (visualizationType) {
			case BlockVisualization.NUMBER:
				return VisualizationNumber.recordLimit;
			case BlockVisualization.LINE_CHART:
				return VisualizationLineChart.recordLimit;
			case BlockVisualization.BAR_CHART:
				return VisualizationBarChart.recordLimit;
			case BlockVisualization.FUNNEL:
				return VisualizationFunnel.recordLimit;
			case BlockVisualization.PIPELINE:
				return VisualizationPipeline.recordLimit;
			case BlockVisualization.PROGRESS:
				return VisualizationProgress.recordLimit;
			case BlockVisualization.TABLE:
				return VisualizationTable.recordLimit;
			case BlockVisualization.PIE:
				return VisualizationPie.recordLimit;
			case BlockVisualization.RADIAL_PROGRESS:
				return VisualizationRadialProgress.recordLimit;
			case BlockVisualization.HORIZONTAL_BAR_CHART:
				return VisualizationHorizontalBarChart.recordLimit;
			case BlockVisualization.DATA_STORY:
				return VisualizationDataStory.recordLimit;
			case BlockVisualization.HEATMAP:
				return VisualizationHeatmap.recordLimit;
			case BlockVisualization.BUBBLE:
				return VisualizationBubble.recordLimit;
			case BlockVisualization.SPIDER:
				return VisualizationSpider.recordLimit;
			case BlockVisualization.GAUGE:
				return VisualizationGauge.recordLimit;
			case BlockVisualization.TEXT:
				return VisualizationText.recordLimit;
			case BlockVisualization.IMAGE:
				return VisualizationImage.recordLimit;
			case BlockVisualization.AI_SUMMARY:
				return VisualizationAiSummary.recordLimit;
			default:
				return VisualizationNumber.recordLimit;
		}
	}
}
